import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Clock, FileText, AlertCircle, CheckCircle, User, Save, Shield, Download, ExternalLink, Monitor, RefreshCw, LogOut
} from 'lucide-react';
import {
  getPublicExam,
  startExamSession,
  getExamByUniqueCandidateId,
  startExamWithCandidateCode,
  getExamByInvitationToken,
  startExamByInvitationToken,
  checkInvitationStatus,
  saveAnswer,
  submitExam,
  isSafeExamBrowser,
  getSebsUrl,
  getSebsUrlForInvitation,
  getSwapStatus,
  swapQuestions,
  type ExamApiError,
  type SwapStatus,
  type InvitationExamInfo,
  type InvitationStatus
} from '../lib/examApi';
import type { ExamSet, ExamQuestion } from '../types/exam';

type ViewState = 'intro' | 'exam' | 'review' | 'submitted' | 'error' | 'expired' | 'seb_required';

interface ErrorDetails {
  error_code?: string;
  exam_title?: string;
  started_at?: string;
  expired_at?: string;
  submitted_at?: string;
  message_detail?: string;
}

const ExamTaking: React.FC = () => {
  const { publicLink, uniqueCandidateId: routeUniqueCandidateId, candidateCode, invitationToken } = useParams<{
    publicLink?: string;
    uniqueCandidateId?: string;
    candidateCode?: string;
    invitationToken?: string;
  }>();

  // Exam info
  const [examSet, setExamSet] = useState<ExamSet | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<InvitationExamInfo | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);

  // Session
  const [viewState, setViewState] = useState<ViewState>('intro');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [anonymousId, setAnonymousId] = useState('');
  const [uniqueCandidateId, setUniqueCandidateId] = useState<string | null>(null);

  // Candidate info (for candidate code flow)
  const [candidateInfo, setCandidateInfo] = useState<{ full_name: string; candidate_code: string } | null>(null);

  // Answers
  const [answers, setAnswers] = useState<Record<string, {
    answer_text?: string;
    selected_option?: number;
  }>>({});
  const [savedStatus, setSavedStatus] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  // Timer
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // SEB
  const [sebNotInstalled, setSebNotInstalled] = useState(false);
  const [sebLaunching, setSebLaunching] = useState(false);
  const [sebAutoLaunchAttempted, setSebAutoLaunchAttempted] = useState(false);

  // Question swap
  const [swapStatus, setSwapStatus] = useState<SwapStatus | null>(null);
  const [showSwapConfirm, setShowSwapConfirm] = useState(false);
  const [swapping, setSwapping] = useState(false);

  // SEB completion detection (for invitation flow)
  const [sebCompletionStatus, setSebCompletionStatus] = useState<InvitationStatus | null>(null);

  // Post-exam contact info
  const [contactEmail, setContactEmail] = useState('');
  const [contactMobile, setContactMobile] = useState('');
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // Auto-save debounce timers
  const [saveTimers, setSaveTimers] = useState<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    if (invitationToken) {
      // Invitation flow: Load exam info using token
      loadExamByInvitationToken();
    } else if (candidateCode) {
      // Candidate code flow: Load exam info for candidate
      loadExamByCandidateCode();
    } else if (routeUniqueCandidateId) {
      // New flow: Load existing session directly
      loadExamByUniqueId();
    } else if (publicLink) {
      // Old flow: Show intro, user enters ID
      loadExam();
    }
  }, [publicLink, routeUniqueCandidateId, candidateCode, invitationToken]);

  // Fetch swap status when session starts
  useEffect(() => {
    async function loadSwapStatus() {
      if (sessionId && anonymousId && viewState === 'exam') {
        try {
          const status = await getSwapStatus(sessionId, anonymousId);
          setSwapStatus(status);
        } catch (err) {
          console.error('Failed to load swap status:', err);
          // Don't show swap button if we can't get status
          setSwapStatus(null);
        }
      }
    }
    loadSwapStatus();
  }, [sessionId, anonymousId, viewState]);

  // Auto-launch SEB when required
  useEffect(() => {
    // For invitation flow, use invitation token URL; otherwise use publicLink
    const canLaunchSeb = invitationToken || publicLink || invitationInfo?.exam.public_link;

    if (viewState === 'seb_required' && !sebAutoLaunchAttempted && canLaunchSeb) {
      // Mark that we've attempted auto-launch
      setSebAutoLaunchAttempted(true);
      setSebLaunching(true);
      setSebNotInstalled(false);

      // Try to open SEB automatically - use invitation URL if available
      let sebUrl: string;
      if (invitationToken) {
        // Invitation flow: use invitation token URL so candidate goes directly to exam
        sebUrl = getSebsUrlForInvitation(invitationToken);
      } else {
        // Standard flow: use publicLink
        const sebPublicLink = publicLink || invitationInfo?.exam.public_link;
        sebUrl = getSebsUrl(sebPublicLink!);
      }
      window.location.href = sebUrl;

      // If we're still on this page after 5 seconds, SEB is likely not installed
      const timeoutId = setTimeout(() => {
        setSebLaunching(false);
        setSebNotInstalled(true);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [viewState, sebAutoLaunchAttempted, publicLink, invitationToken, invitationInfo]);

  // Poll for exam completion when waiting for SEB (invitation flow only)
  useEffect(() => {
    if (viewState !== 'seb_required' || !invitationToken || !sebLaunching) {
      return;
    }

    // Poll every 5 seconds to check exam status
    const pollInterval = setInterval(async () => {
      try {
        const status = await checkInvitationStatus(invitationToken);
        // Update UI for any status change from 'invited'
        if (status.status !== 'invited') {
          setSebCompletionStatus(status);
          // Only stop the "launching" spinner for completed statuses
          if (status.status === 'exam_completed' || status.status === 'graded' || status.status === 'claimed') {
            setSebLaunching(false);
          }
        }
      } catch (err) {
        // Ignore errors during polling
        console.log('Status check failed, continuing to poll...');
      }
    }, 5000);

    // Also check status when window regains focus (after SEB closes)
    const handleFocus = async () => {
      try {
        const status = await checkInvitationStatus(invitationToken);
        // Update UI for any status change from 'invited'
        if (status.status !== 'invited') {
          setSebCompletionStatus(status);
          setSebLaunching(false); // Always stop spinner on focus (SEB was closed)
        }
      } catch (err) {
        console.log('Focus check failed');
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [viewState, invitationToken, sebLaunching]);

  // Timer countdown
  useEffect(() => {
    if (viewState === 'exam' && timeRemaining !== null && timeRemaining > 0) {
      const interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 1) {
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [viewState, timeRemaining]);

  const loadExam = async () => {
    if (!publicLink) return;

    try {
      setLoading(true);
      const exam = await getPublicExam(publicLink);
      setExamSet(exam);
      setError(null);

      // Check if SEB is required and we're not in SEB
      if (exam.seb_required && !isSafeExamBrowser()) {
        setViewState('seb_required');
      }
    } catch (err: any) {
      setError(err.message);
      setViewState('error');
    } finally {
      setLoading(false);
    }
  };

  const loadExamByCandidateCode = async () => {
    if (!candidateCode) return;

    try {
      setLoading(true);
      const data = await startExamWithCandidateCode(candidateCode);

      // Set session info
      setSessionId(data.session.id);
      setUniqueCandidateId(data.session.unique_candidate_id);
      setAnonymousId(data.session.anonymous_id || candidateCode);

      // Set candidate info
      setCandidateInfo(data.candidate_info);

      // Set exam info
      setExamSet({
        id: data.session.exam_set_id,
        title: data.exam_info.title,
        description: data.exam_info.description,
        time_limit_minutes: data.exam_info.time_limit_minutes
      } as ExamSet);

      setQuestions(data.questions);

      // Initialize answers
      const initialAnswers: Record<string, any> = {};
      data.questions.forEach(q => {
        if (q.id) {
          initialAnswers[q.id] = {};
        }
      });
      setAnswers(initialAnswers);

      // Set timer if time limit exists
      if (data.exam_info.time_limit_minutes) {
        setTimeRemaining(data.exam_info.time_limit_minutes * 60);
      }

      setViewState('exam');
      setError(null);
      setErrorDetails(null);
    } catch (err: any) {
      const apiError = err as ExamApiError;
      setError(apiError.message);

      // Handle ALREADY_SUBMITTED - show contact form
      if (apiError.error_code === 'ALREADY_SUBMITTED') {
        // Set session info from error response to show contact form
        const responseData = (err as any);
        if (responseData.session_id) {
          setSessionId(responseData.session_id);
          setAnonymousId(responseData.anonymous_id || candidateCode);
          setUniqueCandidateId(responseData.unique_candidate_id);
          if (responseData.exam_info) {
            setExamSet({ title: responseData.exam_info.title } as ExamSet);
          }
          // If contact already submitted, show success message
          if (responseData.contact_submitted) {
            setContactSubmitted(true);
          }
          setViewState('submitted');
          setError(null);
          return;
        }
      }

      if (apiError.error_code === 'EXAM_EXPIRED' || apiError.error_code === 'EXAM_SUBMITTED' || apiError.error_code === 'EXAM_GRADED') {
        setErrorDetails({
          error_code: apiError.error_code,
          exam_title: apiError.exam_info?.title,
          started_at: apiError.exam_info?.started_at,
          expired_at: apiError.exam_info?.expired_at,
          submitted_at: apiError.exam_info?.submitted_at,
          message_detail: apiError.message_detail
        });
        setViewState('expired');
      } else {
        setViewState('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadExamByUniqueId = async () => {
    if (!routeUniqueCandidateId) return;

    try {
      setLoading(true);
      const data = await getExamByUniqueCandidateId(routeUniqueCandidateId);

      // Set session info
      setSessionId(data.session.id);
      setUniqueCandidateId(data.session.unique_candidate_id);
      setAnonymousId(data.session.anonymous_id || '');

      // Set exam info
      setExamSet({
        id: data.session.exam_set_id,
        title: data.exam_info.title,
        description: data.exam_info.description,
        time_limit_minutes: data.exam_info.time_limit_minutes,
        pass_threshold: data.exam_info.pass_threshold
      } as ExamSet);

      setQuestions(data.questions);

      // Initialize answers
      const initialAnswers: Record<string, any> = {};
      data.questions.forEach(q => {
        if (q.id) {
          initialAnswers[q.id] = {};
        }
      });
      setAnswers(initialAnswers);

      // Set timer - use time_remaining from server if available
      if (data.exam_info.time_remaining !== undefined) {
        setTimeRemaining(data.exam_info.time_remaining * 60); // convert minutes to seconds
      } else if (data.exam_info.time_limit_minutes) {
        setTimeRemaining(data.exam_info.time_limit_minutes * 60);
      }

      setViewState('exam');
      setError(null);
    } catch (err: any) {
      setError(err.message);
      setViewState('error');
    } finally {
      setLoading(false);
    }
  };

  const loadExamByInvitationToken = async () => {
    if (!invitationToken) return;

    try {
      setLoading(true);

      // First, get exam info to check if SEB is required
      const info = await getExamByInvitationToken(invitationToken);
      setInvitationInfo(info);

      // Check if SEB is required and we're not in SEB
      if (info.exam.seb_required && !isSafeExamBrowser()) {
        setExamSet({
          id: info.exam.id,
          title: info.exam.title,
          description: info.exam.description,
          time_limit_minutes: info.exam.time_limit_minutes,
          seb_required: true
        } as ExamSet);
        setCandidateInfo({
          full_name: info.candidate.full_name,
          candidate_code: info.candidate.candidate_code
        });
        setViewState('seb_required');
        setLoading(false);
        return;
      }

      // Start exam session with invitation token
      const data = await startExamByInvitationToken(invitationToken);

      // Set session info
      setSessionId(data.session.id);
      setUniqueCandidateId(data.session.unique_candidate_id);
      setAnonymousId(data.session.anonymous_id || data.candidate_info.candidate_code);

      // Set candidate info
      setCandidateInfo(data.candidate_info);

      // Pre-fill contact info from stored data
      if (data.candidate_info.email) {
        setContactEmail(data.candidate_info.email);
      }
      if (data.candidate_info.mobile_number) {
        setContactMobile(data.candidate_info.mobile_number);
      }

      // Set exam info
      setExamSet({
        id: data.session.exam_set_id,
        title: data.exam_info.title,
        description: data.exam_info.description,
        time_limit_minutes: data.exam_info.time_limit_minutes
      } as ExamSet);

      setQuestions(data.questions);

      // Initialize answers
      const initialAnswers: Record<string, any> = {};
      data.questions.forEach(q => {
        if (q.id) {
          initialAnswers[q.id] = {};
        }
      });
      setAnswers(initialAnswers);

      // Set timer if time limit exists
      if (data.exam_info.time_limit_minutes) {
        setTimeRemaining(data.exam_info.time_limit_minutes * 60);
      }

      setViewState('exam');
      setError(null);
      setErrorDetails(null);
    } catch (err: any) {
      const apiError = err as ExamApiError;
      setError(apiError.message);

      // Handle ALREADY_SUBMITTED - show contact form
      if (apiError.error_code === 'ALREADY_SUBMITTED') {
        const responseData = (err as any);
        if (responseData.session_id) {
          setSessionId(responseData.session_id);
          setAnonymousId(responseData.anonymous_id || '');
          setUniqueCandidateId(responseData.unique_candidate_id);
          // Contact is auto-submitted for invitation flow
          setContactSubmitted(true);
          setViewState('submitted');
          setError(null);
          return;
        }
      }

      if (apiError.error_code === 'EXAM_EXPIRED' || apiError.error_code === 'EXAM_SUBMITTED' || apiError.error_code === 'EXAM_GRADED') {
        setErrorDetails({
          error_code: apiError.error_code,
          exam_title: apiError.exam_info?.title,
          started_at: apiError.exam_info?.started_at,
          expired_at: apiError.exam_info?.expired_at,
          submitted_at: apiError.exam_info?.submitted_at,
          message_detail: apiError.message_detail
        });
        setViewState('expired');
      } else {
        setViewState('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = async () => {
    if (!publicLink || !anonymousId.trim()) {
      setError('Vennligst skriv inn kandidat-ID');
      return;
    }

    try {
      setLoading(true);
      const data = await startExamSession(publicLink, {
        anonymous_id: anonymousId.trim()
      });

      setSessionId(data.session.id);
      setUniqueCandidateId(data.session.unique_candidate_id);
      setQuestions(data.questions);

      // Initialize answers
      const initialAnswers: Record<string, any> = {};
      data.questions.forEach(q => {
        if (q.id) {
          initialAnswers[q.id] = {};
        }
      });
      setAnswers(initialAnswers);

      // Set timer if time limit exists
      if (data.exam_info.time_limit_minutes) {
        setTimeRemaining(data.exam_info.time_limit_minutes * 60);
      }

      setViewState('exam');
      setError(null);
    } catch (err: any) {
      setError(err.message);
      setViewState('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: { answer_text?: string; selected_option?: number }) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setSavedStatus(prev => ({
      ...prev,
      [questionId]: false
    }));

    // Clear existing timer for this question
    if (saveTimers[questionId]) {
      clearTimeout(saveTimers[questionId]);
    }

    // Auto-save after 2 seconds of no changes
    const timeoutId = setTimeout(() => {
      handleSaveAnswer(questionId);
    }, 2000);

    setSaveTimers(prev => ({
      ...prev,
      [questionId]: timeoutId
    }));
  };

  const handleSaveAnswer = async (questionId: string) => {
    if (!sessionId || !anonymousId) return;

    const answer = answers[questionId];
    if (!answer) return;

    try {
      console.log('[ExamTaking] Saving answer for question:', questionId);
      await saveAnswer(sessionId, anonymousId, {
        anonymous_id: anonymousId,
        question_id: questionId,
        ...answer
      });
      console.log('[ExamTaking] Answer saved successfully for:', questionId);
      setSavedStatus(prev => ({
        ...prev,
        [questionId]: true
      }));
    } catch (err: any) {
      console.error('[ExamTaking] Save answer error:', err);
      setSavedStatus(prev => ({
        ...prev,
        [questionId]: false
      }));
      // Error will be shown via the saved status indicator
    }
  };

  const handleAutoSubmit = async () => {
    if (!sessionId) return;

    try {
      await submitExam(sessionId, anonymousId);
      setViewState('submitted');
    } catch (err: any) {
      console.error('Auto-submit failed:', err);
      setError('Automatisk innlevering feilet: ' + err.message);
      setViewState('error');
    }
  };

  const handlePrepareSubmit = async () => {
    if (!sessionId) return;

    // Save all answers before showing review
    setSubmitting(true);
    const savePromises = questions.map(q => {
      if (q.id && answers[q.id]) {
        return handleSaveAnswer(q.id).catch(err => {
          console.error(`Failed to save answer for question ${q.id}:`, err);
        });
      }
      return Promise.resolve();
    });

    await Promise.all(savePromises);
    setSubmitting(false);

    // Show review screen
    setViewState('review');
  };

  const handleFinalSubmit = async () => {
    if (!sessionId) return;

    try {
      setSubmitting(true);
      await submitExam(sessionId, anonymousId);
      setViewState('submitted');
    } catch (err: any) {
      console.error('Submit error:', err);
      setError('Kunne ikke levere prøven: ' + err.message);
      setViewState('exam');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToExam = () => {
    setViewState('exam');
  };

  // Handle contact info submission for receipt
  const handleSubmitContactInfo = async () => {
    if (!sessionId) return;
    if (!contactEmail && !contactMobile) {
      setContactError('Fyll inn e-post og/eller mobilnummer');
      return;
    }

    // Validate mobile format if provided
    if (contactMobile && !/^\+47\d{8}$/.test(contactMobile.replace(/\s/g, ''))) {
      setContactError('Mobilnummer må være i format +47 XXXXXXXX');
      return;
    }

    // Validate email if provided
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setContactError('Ugyldig e-postadresse');
      return;
    }

    setSubmittingContact(true);
    setContactError(null);

    try {
      const response = await fetch(`/api/exam/${sessionId}/submit-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_number: contactMobile.replace(/\s/g, '') || undefined,
          email: contactEmail || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke sende kontaktinformasjon');
      }

      setContactSubmitted(true);
    } catch (err: any) {
      console.error('Contact submission error:', err);
      setContactError(err.message || 'Kunne ikke sende kontaktinformasjon');
    } finally {
      setSubmittingContact(false);
    }
  };

  const handleSwapQuestions = async () => {
    if (!sessionId || !anonymousId) return;

    // If there are answers, show confirmation dialog first
    if (swapStatus?.has_answers && !showSwapConfirm) {
      setShowSwapConfirm(true);
      return;
    }

    try {
      setSwapping(true);
      setShowSwapConfirm(false);

      const result = await swapQuestions(sessionId, anonymousId);

      // Update questions with new set
      setQuestions(result.questions);

      // Clear all answers in local state
      const newAnswers: Record<string, any> = {};
      result.questions.forEach(q => {
        if (q.id) {
          newAnswers[q.id] = {};
        }
      });
      setAnswers(newAnswers);
      setSavedStatus({});

      // Update swap status
      setSwapStatus({
        can_swap: result.swaps_remaining > 0,
        swap_count: result.swap_count,
        swaps_remaining: result.swaps_remaining,
        has_answers: false,
        warning: null
      });

      // Show success message briefly
      setError(null);

    } catch (err: any) {
      console.error('Swap error:', err);
      setError('Kunne ikke bytte spørsmål: ' + err.message);
    } finally {
      setSwapping(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      multiple_choice: 'Flervalg',
      written: 'Skriftlig svar',
      true_false: 'Sant/Usant'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500 text-lg">Laster prøve...</div>
      </div>
    );
  }

  if (viewState === 'error') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-red-900 mb-2">Kunne ikke laste prøve</h2>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (viewState === 'expired') {
    const formatDateTime = (dateStr?: string) => {
      if (!dateStr) return 'Ukjent';
      return new Date(dateStr).toLocaleString('nb-NO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
          <Clock size={64} className="mx-auto text-amber-600 mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {errorDetails?.error_code === 'EXAM_EXPIRED' && 'Tiden har utløpt'}
            {errorDetails?.error_code === 'EXAM_SUBMITTED' && 'Prøven er levert'}
            {errorDetails?.error_code === 'EXAM_GRADED' && 'Prøven er sensurert'}
          </h2>

          {errorDetails?.exam_title && (
            <p className="text-lg text-gray-700 mb-4">{errorDetails.exam_title}</p>
          )}

          <p className="text-gray-700 mb-6">{error}</p>

          {errorDetails?.message_detail && (
            <p className="text-gray-600 mb-6">{errorDetails.message_detail}</p>
          )}

          <div className="bg-white rounded-lg p-4 border border-gray-200 text-left space-y-2">
            {errorDetails?.started_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Startet:</span>
                <span className="font-medium">{formatDateTime(errorDetails.started_at)}</span>
              </div>
            )}
            {errorDetails?.expired_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tid utløpt:</span>
                <span className="font-medium">{formatDateTime(errorDetails.expired_at)}</span>
              </div>
            )}
            {errorDetails?.submitted_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Levert:</span>
                <span className="font-medium">{formatDateTime(errorDetails.submitted_at)}</span>
              </div>
            )}
          </div>

          {errorDetails?.error_code === 'EXAM_GRADED' && (
            <p className="text-sm text-gray-500 mt-6">
              Du vil motta en e-post med resultatet ditt.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (viewState === 'submitted') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle size={64} className="mx-auto text-green-600 mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Prøve innlevert!</h2>
          <p className="text-lg text-gray-700 mb-6">
            Din prøve er mottatt og vil bli vurdert. Du vil få tilbakemelding fra din kursleverandør.
          </p>
          {uniqueCandidateId && (
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
              <div className="text-sm text-gray-600 mb-1">Din kandidat-ID</div>
              <div className="font-mono text-lg text-gray-900 break-all">{uniqueCandidateId}</div>
              <p className="text-xs text-gray-500 mt-2">
                Ta vare på denne ID-en for å sjekke resultatet ditt senere
              </p>
            </div>
          )}

          {/* Contact info form for receipt */}
          {!contactSubmitted ? (
            <div className="bg-white rounded-lg p-6 border border-gray-200 text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Få kvittering på e-post
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Fyll inn e-postadresse for å motta <strong>bekreftelse på innlevert besvarelse</strong> og resultat når sensur er klar.
              </p>

              {contactError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">
                  {contactError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobilnummer (valgfritt)
                  </label>
                  <input
                    type="tel"
                    placeholder="+47 XXX XX XXX"
                    value={contactMobile}
                    onChange={(e) => setContactMobile(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: +47 12345678</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-postadresse (valgfritt)
                  </label>
                  <input
                    type="email"
                    placeholder="din@epost.no"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <button
                  onClick={handleSubmitContactInfo}
                  disabled={submittingContact || (!contactEmail && !contactMobile)}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
                >
                  {submittingContact ? 'Sender...' : 'Send meg kvittering'}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <CheckCircle size={48} className="mx-auto text-blue-600 mb-4" />
              <p className="text-blue-800 font-semibold text-lg mb-2">Kontaktinformasjon registrert!</p>
              <p className="text-blue-700 mb-6">
                Du vil motta bekreftelse på e-post og resultat når sensur er klar.
              </p>

              {/* Exit button - triggers SEB quit URL */}
              <button
                onClick={() => {
                  // Navigate to quit URL to close SEB
                  window.location.href = '/exam/quit';
                }}
                className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                <LogOut size={24} />
                Avslutt prøven
              </button>
              <p className="text-xs text-gray-500 mt-3">
                Klikk for å lukke Safe Exam Browser
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (viewState === 'seb_required' && examSet) {
    // Show completion message if exam was completed in SEB
    if (sebCompletionStatus && (sebCompletionStatus.status === 'exam_completed' || sebCompletionStatus.status === 'graded' || sebCompletionStatus.status === 'claimed')) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-8">
            <div className="text-center mb-8">
              <CheckCircle size={80} className="mx-auto text-green-600 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Eksamen fullført!
              </h1>
              <p className="text-lg text-gray-600 mt-4">
                Takk, <strong>{sebCompletionStatus.full_name}</strong>! Du har fullført eksamen.
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                  <span className="text-green-900">
                    <strong>{sebCompletionStatus.exam_title}</strong> er innlevert
                  </span>
                </div>
                {sebCompletionStatus.receipt_sent && (
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                    <span className="text-green-900">Kvittering sendt på e-post</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Hva skjer nå?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">1.</span>
                  Din besvarelse blir sensurert
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">2.</span>
                  Du mottar e-post og SMS når resultatet er klart
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">3.</span>
                  Ved bestått får du lenke til din digitale kompetanseattest
                </li>
              </ul>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              Du kan nå lukke dette vinduet.
            </p>
          </div>
        </div>
      );
    }

    // Show "exam in progress" message if exam was started but not completed
    // This happens if candidate closed SEB without submitting
    if (sebCompletionStatus && sebCompletionStatus.status === 'exam_started') {
      return (
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-8">
            <div className="text-center mb-8">
              <AlertCircle size={80} className="mx-auto text-amber-500 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Eksamen pågår
              </h1>
              <p className="text-lg text-gray-600 mt-4">
                Din eksamen <strong>{sebCompletionStatus.exam_title}</strong> er påbegynt.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">
                    Forlot du Safe Exam Browser?
                  </h4>
                  <p className="text-sm text-amber-800 mb-3">
                    Dersom du lukket Safe Exam Browser før du leverte inn eksamen, er prøven fortsatt aktiv med en nedtellende tidsfrist.
                  </p>
                  <p className="text-sm text-amber-800">
                    <strong>Viktig:</strong> Det er ikke tillatt å forlate SEB under prøven. Hvis tiden går ut, leveres besvarelsen automatisk med svarene som var registrert.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Hva kan du gjøre?</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span><strong>Fortsett i SEB:</strong> Klikk knappen nedenfor for å åpne SEB og fortsette der du slapp (hvis tiden ikke har utløpt).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span><strong>Kontakt kursleverandør:</strong> Hvis du trenger ny prøve, må du kontakte arrangøren som kan tilbakestille eksamen.</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => {
                if (invitationToken) {
                  window.location.href = getSebsUrlForInvitation(invitationToken);
                }
              }}
              className="w-full py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-3"
            >
              <ExternalLink size={24} />
              Åpne Safe Exam Browser og fortsett
            </button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Denne siden oppdateres automatisk når eksamen er innlevert.
            </p>
          </div>
        </div>
      );
    }

    const handleOpenInSeb = () => {
      const canOpen = invitationToken || publicLink || invitationInfo?.exam.public_link;
      if (!canOpen) return;

      setSebLaunching(true);
      setSebNotInstalled(false);

      // Try to open sebs:// URL - use invitation URL if available
      let sebUrl: string;
      if (invitationToken) {
        sebUrl = getSebsUrlForInvitation(invitationToken);
      } else {
        const sebPublicLink = publicLink || invitationInfo?.exam.public_link;
        sebUrl = getSebsUrl(sebPublicLink!);
      }
      window.location.href = sebUrl;

      // If we're still on this page after 5 seconds, SEB is likely not installed
      setTimeout(() => {
        setSebLaunching(false);
        setSebNotInstalled(true);
      }, 5000);
    };

    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-8">
          <div className="text-center mb-8">
            <Shield size={64} className="mx-auto text-blue-600 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Denne prøven krever Safe Exam Browser
            </h1>
            <p className="text-lg text-gray-600 mt-4">
              For å gjennomføre <strong>{examSet.title}</strong> må du bruke Safe Exam Browser (SEB),
              som låser datamaskinen under prøvegjennomføringen for å sikre rettferdige vilkår.
            </p>
          </div>

          {/* Show launching state */}
          {sebLaunching && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
                <div className="text-center">
                  <h3 className="font-semibold text-blue-900 text-lg">Starter Safe Exam Browser...</h3>
                  <p className="text-sm text-blue-700 mt-2">
                    Vennligst vent mens Safe Exam Browser starter. Hvis du blir bedt om å tillate åpning av SEB, klikk "Åpne" eller "Tillat".
                  </p>
                  <p className="text-xs text-blue-600 mt-3">
                    Denne siden oppdateres automatisk når eksamen er fullført.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Show info box when not launching */}
          {!sebLaunching && !sebNotInstalled && (
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Monitor size={20} className="text-blue-600" />
                Hva gjør Safe Exam Browser?
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  Låser datamaskinen slik at kun prøven er tilgjengelig
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  Blokkerer tilgang til andre programmer, nettsider og søkemotorer
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  Forhindrer skjermopptak og utklippstavle
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  Sikrer at prøven gjennomføres under kontrollerte forhold
                </li>
              </ul>
            </div>
          )}

          {/* Manual open button */}
          {!sebLaunching && (
            <button
              onClick={handleOpenInSeb}
              className="w-full py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-3"
            >
              <ExternalLink size={24} />
              {sebAutoLaunchAttempted ? 'Prøv å åpne Safe Exam Browser på nytt' : 'Åpne i Safe Exam Browser'}
            </button>
          )}

          {sebNotInstalled && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle size={24} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-2">
                    Safe Exam Browser ser ikke ut til å være installert
                  </h4>
                  <p className="text-sm text-yellow-800 mb-4">
                    Vi prøvde å starte SEB automatisk, men det ser ut til at den ikke er installert på denne enheten. Last ned og installer SEB for din plattform:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://safeexambrowser.org/download_en.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      <Download size={16} />
                      Windows / macOS - safeexambrowser.org
                    </a>
                    <a
                      href="https://apps.apple.com/app/safe-exam-browser/id1104085460"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      <Download size={16} />
                      iOS / iPadOS - App Store
                    </a>
                  </div>
                  <p className="text-xs text-yellow-700 mt-4">
                    Etter installasjon, kom tilbake til denne siden og klikk "Prøv å åpne Safe Exam Browser på nytt".
                  </p>
                </div>
              </div>
            </div>
          )}

          {!sebNotInstalled && !sebLaunching && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Har du ikke SEB installert?{' '}
              <button
                onClick={() => setSebNotInstalled(true)}
                className="text-blue-600 hover:underline"
              >
                Vis installasjonsveiledning
              </button>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (viewState === 'intro' && examSet) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-8">
          <div className="text-center mb-8">
            <FileText size={64} className="mx-auto text-blue-600 mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{examSet.title}</h1>
            {examSet.description && (
              <p className="text-lg text-gray-600 mt-4">{examSet.description}</p>
            )}
          </div>

          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Prøveinformasjon</h3>
            <div className="space-y-3 text-sm">
              {examSet.time_limit_minutes && (
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Tidsbegrensning:</strong> {examSet.time_limit_minutes} minutter
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-blue-600" />
                <span className="text-gray-700">
                  <strong>Grense for bestått:</strong> {examSet.pass_threshold}%
                </span>
              </div>
              {examSet.validity_years && (
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Gyldighet:</strong> {examSet.validity_years} år
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-2">
              <AlertCircle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <strong>Viktig:</strong> Du kan kun ta denne prøven én gang.
                Svar vil bli lagret underveis, men du må levere inn prøven før tiden går ut.
                {examSet.time_limit_minutes && (
                  <> Prøven vil bli automatisk innlevert når tiden er ute.</>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User size={16} className="inline mr-1" />
              Kandidat-ID
            </label>
            <input
              type="text"
              value={anonymousId}
              onChange={(e) => {
                setAnonymousId(e.target.value);
                if (error) setError(null);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Skriv inn din kandidat-ID fra kursleverandøren"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-2">
              Du skal ha mottatt denne ID-en fra din kursleverandør
            </p>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <button
            onClick={handleStartExam}
            disabled={loading || !anonymousId.trim()}
            className="w-full py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Starter...' : 'Start prøve'}
          </button>
        </div>
      </div>
    );
  }

  if (viewState === 'exam') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Swap confirmation modal */}
          {showSwapConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle size={24} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Bytte spørsmålssett?</h3>
                    <p className="text-gray-600 mt-2">
                      Du har allerede besvart noen spørsmål. Hvis du bytter spørsmålssett, vil <strong>alle dine svar bli slettet</strong> og du må starte på nytt.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-6">
                  <button
                    onClick={() => setShowSwapConfirm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleSwapQuestions}
                    disabled={swapping}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition flex items-center gap-2"
                  >
                    {swapping && <RefreshCw size={16} className="animate-spin" />}
                    Ja, bytt spørsmål
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header with timer */}
          <div className="bg-white rounded-lg border border-gray-200 shadow p-6 mb-6 sticky top-4 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{examSet?.title}</h2>
                <p className="text-sm text-gray-600">
                  Kandidat: {candidateInfo?.full_name || anonymousId}
                  {candidateInfo?.candidate_code && <span className="ml-2 text-gray-400">({candidateInfo.candidate_code})</span>}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {/* Swap button */}
                {swapStatus?.can_swap && (
                  <button
                    onClick={handleSwapQuestions}
                    disabled={swapping}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
                    title={`Bytt spørsmålssett (${swapStatus.swaps_remaining} igjen)`}
                  >
                    <RefreshCw size={16} className={swapping ? 'animate-spin' : ''} />
                    Bytt spørsmål
                  </button>
                )}
                {timeRemaining !== null && (
                  <div className="flex items-center gap-2">
                    <Clock size={24} className={timeRemaining < 300 ? 'text-red-600' : 'text-blue-600'} />
                    <div className={`text-3xl font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatTime(timeRemaining)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {timeRemaining !== null && timeRemaining < 300 && (
              <div className="mt-3 text-sm text-red-600 font-medium">
                ⚠️ Mindre enn 5 minutter igjen!
              </div>
            )}
          </div>

          {/* SEB Warning Banner */}
          {examSet?.seb_required && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Viktig om Safe Exam Browser (SEB)</h3>
                  <ul className="mt-2 text-sm text-amber-700 space-y-1">
                    <li>• <strong>Ikke lukk nettleservinduet</strong> før du har fullført og levert prøven.</li>
                    <li>• Hvis du lukker SEB før prøven er ferdig, blir prøven automatisk levert med svarene du har gitt så langt.</li>
                    <li>• Du vil <strong>ikke</strong> kunne fortsette prøven etter at SEB er lukket – du må i så fall kontakte kursleverandøren for å få ny prøve.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Questions */}
          <div className="space-y-6">
            {questions.map((question, index) => {
              const questionId = question.id || '';
              const answer = answers[questionId] || {};
              const isSaved = savedStatus[questionId];

              return (
                <div key={questionId} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  {/* Question header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-gray-900">Spørsmål {index + 1}</span>
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {getQuestionTypeLabel(question.question_type)}
                          </span>
                          <span className="text-sm text-gray-600">
                            {Number(question.points)} {Number(question.points) === 1 ? 'poeng' : 'poeng'}
                          </span>
                        </div>
                        <p className="text-gray-900">{question.question_text}</p>
                      </div>
                      {isSaved && (
                        <div className="ml-4 flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle size={16} />
                          <span>Lagret</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Answer area */}
                  <div className="p-6">
                    {/* Multiple Choice */}
                    {question.question_type === 'multiple_choice' && question.options && (
                      <div className="space-y-3">
                        {question.options.map((option, optIndex) => (
                          <label
                            key={optIndex}
                            className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${
                              answer.selected_option === optIndex
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${questionId}`}
                              checked={answer.selected_option === optIndex}
                              onChange={() => handleAnswerChange(questionId, { selected_option: optIndex })}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="ml-3 text-gray-900">{option.text}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* True/False */}
                    {question.question_type === 'true_false' && question.options && (
                      <div className="space-y-3">
                        {question.options.map((option, optIndex) => (
                          <label
                            key={optIndex}
                            className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${
                              answer.selected_option === optIndex
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${questionId}`}
                              checked={answer.selected_option === optIndex}
                              onChange={() => handleAnswerChange(questionId, { selected_option: optIndex })}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="ml-3 text-gray-900">{option.text}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Written Answer */}
                    {question.question_type === 'written' && (
                      <div>
                        <textarea
                          value={answer.answer_text || ''}
                          onChange={(e) => handleAnswerChange(questionId, { answer_text: e.target.value })}
                          rows={8}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Skriv ditt svar her..."
                        />
                      </div>
                    )}

                    {/* Auto-save status indicator */}
                    <div className="mt-4 flex items-center gap-2 text-sm">
                      {isSaved ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={16} />
                          <span>Lagret automatisk</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-500">
                          <Save size={16} />
                          <span>Lagres automatisk...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit button */}
          <div className="mt-8 bg-white rounded-lg border border-gray-200 shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Klar til å levere?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Svarene dine lagres automatisk. Klikk her for å se oppsummering og levere inn.
                </p>
              </div>
              <button
                onClick={handlePrepareSubmit}
                disabled={submitting}
                className="px-6 py-3 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
              >
                {submitting ? 'Forbereder...' : 'Gå til oppsummering'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (viewState === 'review') {
    // Count answered and unanswered questions
    const answeredCount = questions.filter(q => {
      const answer = q.id ? answers[q.id] : null;
      return answer && (answer.answer_text !== undefined || answer.selected_option !== undefined);
    }).length;
    const unansweredCount = questions.length - answeredCount;

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="bg-white rounded-lg border border-gray-200 shadow p-6 mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Oppsummering av besvarelsen</h2>
            <p className="text-gray-600">
              Sjekk over svarene dine før du leverer inn prøven. Du kan gå tilbake og endre svar om nødvendig.
            </p>
            <div className="mt-4 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                <span className="font-medium">{answeredCount} besvarte</span>
              </div>
              {unansweredCount > 0 && (
                <div className="flex items-center gap-2">
                  <AlertCircle size={20} className="text-yellow-600" />
                  <span className="font-medium text-yellow-700">{unansweredCount} ubesvarte</span>
                </div>
              )}
            </div>
          </div>

          {/* Questions Review */}
          <div className="space-y-6">
            {questions.map((question, index) => {
              const questionId = question.id || '';
              const answer = answers[questionId] || {};
              const hasAnswer = answer.answer_text !== undefined || answer.selected_option !== undefined;

              return (
                <div key={questionId} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-gray-900">Spørsmål {index + 1}</span>
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {getQuestionTypeLabel(question.question_type)}
                          </span>
                          <span className="text-sm text-gray-600">
                            {Number(question.points)} poeng
                          </span>
                        </div>
                        <p className="text-gray-900">{question.question_text}</p>
                      </div>
                      {hasAnswer ? (
                        <div className="ml-4 flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle size={16} />
                          <span>Besvart</span>
                        </div>
                      ) : (
                        <div className="ml-4 flex items-center gap-1 text-yellow-600 text-sm">
                          <AlertCircle size={16} />
                          <span>Ikke besvart</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Multiple Choice / True False Answer */}
                    {(question.question_type === 'multiple_choice' || question.question_type === 'true_false') && question.options && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Ditt svar:</p>
                        {answer.selected_option !== undefined ? (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-gray-900">{question.options[answer.selected_option]?.text || 'Ukjent valg'}</p>
                          </div>
                        ) : (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-800 text-sm">Ingen svar valgt</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Written Answer */}
                    {question.question_type === 'written' && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Ditt svar:</p>
                        {answer.answer_text ? (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-gray-900 whitespace-pre-wrap">{answer.answer_text}</p>
                          </div>
                        ) : (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-800 text-sm">Ingen svar skrevet</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="mt-8 bg-white rounded-lg border border-gray-200 shadow p-6">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handleBackToExam}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 text-lg font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Tilbake til prøven
              </button>
              <div className="flex-1 text-center">
                {unansweredCount > 0 && (
                  <p className="text-sm text-yellow-700">
                    <AlertCircle size={16} className="inline mr-1" />
                    Du har {unansweredCount} ubesvarte spørsmål
                  </p>
                )}
              </div>
              <button
                onClick={handleFinalSubmit}
                disabled={submitting}
                className="px-6 py-3 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
              >
                {submitting ? 'Leverer inn...' : 'Bekreft og lever inn'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ExamTaking;
